import {
    ButtonAnalyticsContext,
    ButtonAnalyticsContextValue,
    useButtonAnalyticsContext,
} from "./ButtonAnalyticsContext";
import { ReactNode, useMemo } from "react";

/**
 * Props for ButtonAnalyticsProvider.
 */
export interface ButtonAnalyticsProviderProps extends ButtonAnalyticsContextValue {
    children?: ReactNode;
    /**
     * should we override locations instead of concat them
     */
    overrideLocations?: boolean;
}

/**
 * Provides button analytics context to its children.
 * Wrap your component tree with this provider to supply analytics metadata
 * (such as namePrefix, applicationFeature, and location) to descendant components.
 */
export const ButtonAnalyticsProvider = ({
    children,
    namePrefix,
    applicationFeature,
    location,
    overrideLocations,
}: ButtonAnalyticsProviderProps) => {
    // get current location
    const { location: currentLocation, applicationFeature: currentFeature } = useButtonAnalyticsContext();

    // set location if exists, or concat to current one
    const fullLocation = location
        ? location + (!overrideLocations && currentLocation ? ` (${currentLocation})` : "")
        : currentLocation;

    // to keep the value reference
    const value = useMemo(() => {
        const actualLocation = overrideLocations ? location : fullLocation;
        return { namePrefix, applicationFeature: applicationFeature ?? currentFeature, location: actualLocation };
    }, [namePrefix, applicationFeature, location, fullLocation, overrideLocations, currentFeature]);

    return <ButtonAnalyticsContext.Provider value={value}>{children}</ButtonAnalyticsContext.Provider>;
};
